import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createSignature, updateSignature, deleteSignature } from '../../api/signatureApi';

import { signaturesKeys } from './keys';

export function useCreateSignature() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (signatureData) => createSignature(signatureData),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: signaturesKeys.all });
		},
	});
}

export function useUpdateSignature() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, updates }) => updateSignature(id, updates),
		onMutate: async ({ id, updates }) => {
			await queryClient.cancelQueries({ queryKey: signaturesKeys.list() });

			const previousSignatures = queryClient.getQueryData(signaturesKeys.list());

			queryClient.setQueryData(signaturesKeys.list(), (old) => {
				if (!old) return old;
				return old.map((sig) => (sig.id === id ? { ...sig, ...updates } : sig));
			});

			return { previousSignatures };
		},
		onError: (err, variables, context) => {
			if (context?.previousSignatures) {
				queryClient.setQueryData(signaturesKeys.list(), context.previousSignatures);
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: signaturesKeys.all });
		},
	});
}

export function useDeleteSignature() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id) => deleteSignature(id),
		onMutate: async (id) => {
			await queryClient.cancelQueries({ queryKey: signaturesKeys.list() });

			const previousSignatures = queryClient.getQueryData(signaturesKeys.list());

			queryClient.setQueryData(signaturesKeys.list(), (old) => {
				if (!old) return old;
				return old.filter((sig) => sig.id !== id);
			});

			return { previousSignatures };
		},
		onError: (err, id, context) => {
			if (context?.previousSignatures) {
				queryClient.setQueryData(signaturesKeys.list(), context.previousSignatures);
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: signaturesKeys.all });
		},
	});
}
